import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { UploadMediaReportRepository } from "../../domain/upload-media-report-repository";
import moment from "moment";
import { Router } from "@angular/router";
import { Socket } from "../../infrastructure/socket.service";
import { TenantSettingsRepository } from "../../domain/tenant-settings-repository";
import { UserService } from "../../domain/user-service";
import { UploadMediaReportItem } from "../../domain/upload-media-report-item";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "uploads-preview",
  templateUrl: "./uploads-report.component.html",
  styleUrls: ["./uploads-report.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadStatusReport implements OnInit, OnDestroy {
  rowsPerPage = 30;
  lastRefresh: moment.Moment;
  pageOffset = 0;
  total = 0;
  rows = [];
  searchQuery = "";
  isLoading = true;
  sorts: {
    prop: "filename" | "date" | "status" | "quality";
    dir: "asc" | "desc";
  }[] = [
    {
      prop: "date",
      dir: "desc",
    },
  ];
  allowedQCAnalyze = false;

  private refreshReportInterval;
  private currentRequest: Promise<any> | null = null;
  private isRequestInProgress = false;
  private requestAbortController: AbortController | null = null;
  private loadedDataRanges: Array<{ start: number; end: number }> = [];
  private cacheKey: string = "";

  constructor(
    private uploadMediaReportRepository: UploadMediaReportRepository,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private socket: Socket,
    private toastr: ToastrService,
    private tenantSettingsRepository: TenantSettingsRepository,
    private userService: UserService,
  ) {
    this.tenantSettingsRepository.getTenantSettings().then((tenantSettings) => {
      const currentUser = this.userService.getCurrentUser();
      this.allowedQCAnalyze =
        tenantSettings.DoQcAnalizeWhenUploading &&
        currentUser.hasQcApprovalPermission;
    });
  }

  ngOnInit() {
    this.socket.hubConnection.on("uploadFinished", () => this.refresh());

    this.refreshReportInterval = setInterval(() => {
      this.refresh();
    }, 30000);
  }

  getStatusIcon(status) {
    switch (status) {
      case "success":
      case "uploading":
      case "converting":
        return "/assets/icons/success.svg";
      case "pending":
        return "/assets/icons/pending.svg";
      case "failed":
      case "deleted":
        return "/assets/icons/failed.svg";
    }
  }

  formatQuality(quality) {
    switch (quality) {
      case 1:
        return "low";
      case 2:
        return "medium";
      case 3:
        return "high";
    }
  }

  formatStatus(status) {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  onkeyup(e: KeyboardEvent) {
    if (e.code === "Enter") {
      this.search();
    }
  }

  formatLastRefresh() {
    return this.lastRefresh.format("ddd D MMMM YYYY | HH:mm:ss");
  }

  formatDate(date: moment.Moment) {
    return date.format("MM-DD-YYYY HH:mm");
  }

  private isDataAlreadyLoaded(start: number, end: number): boolean {
    // Check if the requested range is completely covered by existing loaded ranges
    // and the cache key matches (same search/sort parameters)
    const currentCacheKey = this.generateCacheKey();
    if (this.cacheKey !== currentCacheKey) {
      return false; // Cache is invalid due to parameter changes
    }

    for (const range of this.loadedDataRanges) {
      if (start >= range.start && end <= range.end) {
        return true;
      }
    }
    return false;
  }

  private generateCacheKey(): string {
    // Create a unique key based on search and sort parameters
    return `${this.searchQuery}|${this.sorts[0].prop}|${this.sorts[0].dir}`;
  }

  private calculateLoadRange(
    requestedStart: number,
    requestedEnd: number,
  ): { start: number; count: number } {
    // Calculate how many pages to load to prevent gaps
    const pagesToLoad = 3; // Load current page + 1 page before + 1 page after
    const pageSize = this.rowsPerPage;

    // Calculate the start of the range to load
    const loadStart = Math.max(0, requestedStart - pageSize);

    // Calculate how many items to load
    const loadCount = Math.min(
      pageSize * pagesToLoad, // Maximum 3 pages worth of data
      this.total - loadStart, // Don't exceed total available items
    );

    return {
      start: loadStart,
      count: Math.max(pageSize, loadCount), // At least load the requested page
    };
  }

  private addLoadedDataRange(start: number, end: number) {
    // Add new range and merge overlapping ranges
    const newRange = { start, end };

    // Find overlapping ranges
    const overlappingRanges = this.loadedDataRanges.filter(
      (range) => start <= range.end && end >= range.start,
    );

    if (overlappingRanges.length > 0) {
      // Merge overlapping ranges
      const minStart = Math.min(
        start,
        ...overlappingRanges.map((r) => r.start),
      );
      const maxEnd = Math.max(end, ...overlappingRanges.map((r) => r.end));

      // Remove overlapping ranges
      this.loadedDataRanges = this.loadedDataRanges.filter(
        (range) => !overlappingRanges.includes(range),
      );

      // Add merged range
      this.loadedDataRanges.push({ start: minStart, end: maxEnd });
    } else {
      // No overlap, just add the new range
      this.loadedDataRanges.push(newRange);
    }

    // Sort ranges by start position for easier debugging
    this.loadedDataRanges.sort((a, b) => a.start - b.start);
  }

  async loadReport(force: boolean = false) {
    const start = this.pageOffset * this.rowsPerPage;
    const end = start + this.rowsPerPage - 1;

    // Check if data is already loaded (unless forced refresh)
    if (!force && this.isDataAlreadyLoaded(start, end)) {
      return;
    }

    // Prevent multiple simultaneous requests (unless forced)
    if (this.isRequestInProgress && !force) {
      return;
    }

    // Cancel any existing request
    if (this.requestAbortController) {
      this.requestAbortController.abort();
      this.requestAbortController = null;
    }

    this.isRequestInProgress = true;
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();

    // Create new abort controller for this request
    this.requestAbortController = new AbortController();
    const loadRange = this.calculateLoadRange(start, end);
    try {
      // For now, let's load exactly what the table expects to prevent holes
      this.currentRequest = this.uploadMediaReportRepository.get(
        loadRange.start,
        loadRange.count,
        this.searchQuery,
        this.sorts[0].prop,
        this.sorts[0].dir,
      );

      const uploadReport = await this.currentRequest;

      // Only update if this request wasn't cancelled
      if (this.currentRequest && !this.requestAbortController.signal.aborted) {
        // Create a completely new array instance to ensure change detection
        const newRows = new Array(
          Math.max(
            this.rows.length,
            loadRange.start + uploadReport.items.length,
          ),
        );

        // Copy existing data
        for (let i = 0; i < this.rows.length; i++) {
          newRows[i] = this.rows[i];
        }

        // Fill gaps with null
        for (let i = this.rows.length; i < loadRange.start; i++) {
          newRows[i] = null;
        }

        // Place the new data at the exact position
        for (let i = 0; i < uploadReport.items.length; i++) {
          newRows[loadRange.start + i] = uploadReport.items[i];
        }

        // Assign the new array instance
        this.rows = newRows;
        this.total = uploadReport.total;
        this.lastRefresh = moment();

        // Track the loaded data range
        this.addLoadedDataRange(
          loadRange.start,
          loadRange.start + uploadReport.items.length - 1,
        );

        // Update cache key to match current parameters
        this.cacheKey = this.generateCacheKey();

        // Force change detection
        this.changeDetectorRef.detectChanges();
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error loading report:", error);
      }
    } finally {
      this.isLoading = false;
      this.isRequestInProgress = false;
      this.currentRequest = null;
      this.requestAbortController = null;
      this.changeDetectorRef.markForCheck();
    }
  }

  search() {
    this.pageOffset = 0;
    this.rows = [];
    this.loadedDataRanges = []; // Clear cache for new search
    this.cacheKey = ""; // Invalidate cache key
    this.changeDetectorRef.detectChanges(); // Force change detection
    this.loadReport();
  }

  onSort(event) {
    this.sorts = event.sorts;
    this.rows = [];
    this.loadedDataRanges = []; // Clear cache for new sort
    this.cacheKey = ""; // Invalidate cache key
    this.changeDetectorRef.detectChanges(); // Force change detection
    this.loadReport();
  }

  onPage(event) {
    this.pageOffset = event.offset;
    this.rowsPerPage = event.pageSize;
    this.loadReport();
  }

  showQcButton(mediaId) {
    const row = this.rows.find((x) => x.mediaId === mediaId);
    if (!row) {
      return false;
    }
    return row.quality > 0;
  }

  gotoQc(mediaId: number) {
    this.router.navigate([`mam/qualityCheck/${mediaId}`]);
  }

  refresh() {
    this.loadReport(true);
  }

  ngOnDestroy(): void {
    // console.log('Upload-Report component: clearing the refresh interval');
    clearInterval(this.refreshReportInterval);

    // Cancel any ongoing request
    if (this.requestAbortController) {
      this.requestAbortController.abort();
      this.requestAbortController = null;
    }

    this.currentRequest = null;
    this.isRequestInProgress = false;
    this.loadedDataRanges = [];
    this.cacheKey = "";
  }

  rowActivatedEventHandler(selectedRow: any) {
    if (selectedRow.type === "click") {
      const reportItem: UploadMediaReportItem = selectedRow.row;
      this.router.navigate([`mam/editMedia/${reportItem.mediaId}`]);
    }
  }

  showToastr() {
    this.toastr.success("VIDEO ID has been copied!");
  }
}
